import React, { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import { ClickAwayListener, FormControl } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import LightTooltip from "./LightTooltip";

const SmartPasswordField = (props) => {
  const [capsLockWarning, setCapsLockWarning] = useState(false);
  const [open, setOpen] = useState(false);
  const [realValue, setRealValue] = useState(""); // actual password
  const [originalLength, setOriginalLength] = useState(0);
  const [isFirstEdit, setIsFirstEdit] = useState(true); // track if user hasn't edited yet
  const inputRef = useRef(null);
  const caretPosRef = useRef(null); // 🔑 track caret position



  // helper: build masked display
  const buildDisplay = (val) => {
    const masked = "*".repeat(Math.min(originalLength, val.length));
    const newPart = "*".repeat(val.substring(originalLength).length);
    return masked + newPart;
  };

  // restore caret after render
  useEffect(() => {
    if (caretPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(caretPosRef.current, caretPosRef.current);
      caretPosRef.current = null; // reset
    }
  });

  const handleKeyDown = (e) => {
    const inputEl = e.target;
    const start = inputEl.selectionStart;
    const end = inputEl.selectionEnd;
    let newValue = realValue;
  
    if ((e.key === "Backspace" || e.key === "Delete")) {
      // Clear field on first edit
      if (isFirstEdit) {
        setIsFirstEdit(false);
        setRealValue("");
        setOriginalLength(0);
        caretPosRef.current = 0;
        props.onChange?.({
          ...e,
          target: { ...e.target, value: "", name: e.target.name },
        });
        e.preventDefault();
        return;
      }

      if (start !== end) {
        // Delete whole selection (even full value)
        newValue = realValue.substring(0, start) + realValue.substring(end);
        setOriginalLength((len) => Math.max(0, len - (end - start)));
        caretPosRef.current = start;
      } else if (e.key === "Backspace" && start > 0) {
        newValue = realValue.substring(0, start - 1) + realValue.substring(end);
        setOriginalLength((len) => Math.max(0, len - 1));
        caretPosRef.current = start - 1;
      } else if (e.key === "Delete" && start < realValue.length) {
        newValue = realValue.substring(0, start) + realValue.substring(end + 1);
        setOriginalLength((len) => Math.max(0, len - 1));
        caretPosRef.current = start;
      }
  
      if (newValue !== realValue) {
        setRealValue(newValue);
        props.onChange?.({
          ...e,
          target: { ...e.target, value: newValue, name: e.target.name },
        });
      }
  
      e.preventDefault();
    }
  };
  
  const handleChange = (e) => {
    // Ignore deletions (handled in handleKeyDown)
    if (!e.nativeEvent.data && e.nativeEvent?.inputType?.startsWith("delete")) {
      return;
    }
  
    const inputEl = e.target;
    const start = inputEl.selectionStart;
    const end = inputEl.selectionEnd;
  
    const typedChar = e.nativeEvent.data;
    if (!typedChar) return;

    // Clear field on first edit and start fresh
    if (isFirstEdit) {
      setIsFirstEdit(false);
      setRealValue(typedChar);
      setOriginalLength(0);
      caretPosRef.current = 1;
      props.onChange?.({
        ...e,
        target: { ...e.target, value: typedChar, name: e.target.name },
      });
      return;
    }
  
    const newValue =
      realValue.substring(0, start - 1) + typedChar + realValue.substring(end);
  
    setRealValue(newValue);
    setOriginalLength(originalLength); // preserve mask
    caretPosRef.current = start;
  
    props.onChange?.({
      ...e,
      target: { ...e.target, value: newValue, name: e.target.name },
    });
  };
  

  const handleKeyPress = (e) => {
    const capsLockActive = e.getModifierState("CapsLock");
    setCapsLockWarning(capsLockActive);
    setOpen(capsLockActive);
  };

  // initialize
  useEffect(() => {
    const val = props.value || "";
    setRealValue(val);
    // Use initialMaskLength prop if provided, otherwise mask the entire initial value
    const maskLength = props.initialMaskLength !== undefined ? props.initialMaskLength : val.length;

    setOriginalLength(Math.min(maskLength, val.length));
  }, []);

  // sync if parent changes
  useEffect(() => {
    if (props.value !== undefined && props.value !== realValue) {
      const newValue = props.value;
      setRealValue(newValue);
      setIsFirstEdit(true); // Reset first edit flag when value changes from parent
      
      // If this is not the first time we're setting a value (realValue is not empty),
      // and the new value is different, preserve some visible content
      if (realValue.length > 0) {
        // If new value is longer, assume new content was added - keep existing mask length
        if (newValue.length > realValue.length) {
          // Don't change originalLength, let new content be visible
        } else {
          // If new value is same length or shorter, fully mask the new value
          setOriginalLength(newValue.length);
        }
      } else {
        // First time setting value - respect initialMaskLength if provided
        if (props.initialMaskLength !== undefined) {
          setOriginalLength(Math.min(props.initialMaskLength, newValue.length));
        } else {
          // No initialMaskLength provided, fully mask
          setOriginalLength(newValue.length);
        }
      }
    }
  }, [props.value]);

  // Preserve override-bold coming from either inputProps.style or sx.
  const sxInputFontWeight =
    props?.sx?.input?.fontWeight ||
    props?.sx?.["& .MuiInputBase-input"]?.fontWeight;
  const resolvedFontWeight = props?.inputProps?.style?.fontWeight || sxInputFontWeight;

  return (
    <FormControl className="width-100" size="small">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <LightTooltip
          onClose={() => setOpen(false)}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={
            capsLockWarning && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <WarningIcon /> <span style={{ fontSize: "14px" }}>Caps Lock is On</span>
                </div>
                <div style={{ textAlign: "justify" }}>
                  Having Caps Lock on may cause you to enter your password incorrectly.
                </div>
              </div>
            )
          }
          arrow
        >
          <TextField
            {...props}
            inputRef={inputRef}
            size="small"
            label={props.label || "Password"}
            type="text"
            value={buildDisplay(realValue)}
            onKeyUp={handleKeyPress}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            autoComplete="new-password"
            inputProps={{
              ...props.inputProps,
              style: {
                ...props.inputProps?.style,
                ...(resolvedFontWeight ? { fontWeight: resolvedFontWeight } : {}),
                fontFamily: buildDisplay(realValue).includes("*")
                  ? "monospace"
                  : "inherit",
              },
            }}
          />
        </LightTooltip>
      </ClickAwayListener>
    </FormControl>
  );
};

export default SmartPasswordField;
